import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CaretLeft, CaretRight, CheckCircle, Airplane, CalendarBlank, PaperPlaneTilt, SpinnerGap, X } from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MAX_SELECT = 10;

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function isoDate(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }
function prettyDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Availability calendar shown on public profiles (workers & contractors).
 * Read-only view of booked/available days + tap-to-select available dates
 * and send a pre-filled booking request message.
 */
export default function PublicAvailabilityCalendar({ userId, userName }) {
  const navigate = useNavigate();
  const [blocked, setBlocked] = useState(new Set());
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return { year: t.getFullYear(), month: t.getMonth() };
  });

  const todayObj = new Date();
  const today = isoDate(todayObj.getFullYear(), todayObj.getMonth(), todayObj.getDate());

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/worker-availability/${userId}`);
        if (cancelled) return;
        const a = res.data?.availability || {};
        setBlocked(new Set(a.blocked_dates || []));
        setStatus(res.data?.status || null);
      } catch {
        /* no record yet — treat as fully available */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const vacationUntil = status?.vacation_mode ? status?.vacation_until : null;

  const isBlocked = (iso) => {
    if (blocked.has(iso)) return true;
    if (vacationUntil && iso <= vacationUntil && iso >= today) return true;
    return false;
  };

  const toggleSelect = (iso) => {
    if (iso < today || isBlocked(iso)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) {
        next.delete(iso);
      } else {
        if (next.size >= MAX_SELECT) {
          toast({ title: `Max ${MAX_SELECT} dates`, description: 'Deselect a date to pick another.' });
          return prev;
        }
        next.add(iso);
      }
      return next;
    });
  };

  const sendRequest = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast({ title: 'Login required', description: 'Sign in to request these dates.', variant: 'destructive' });
      navigate('/login');
      return;
    }
    const dates = Array.from(selected).sort();
    const dateList = dates.map(prettyDate).join(', ');
    const content = `Hi${userName ? ' ' + userName.split(' ')[0] : ''}! I saw your availability calendar — are you free on ${dateList}? I'd like to book you for a job. Let me know!`;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/messages`,
        { receiver_id: userId, content, booking_dates: dates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Booking request sent!', description: `Requested ${dates.length} date${dates.length > 1 ? 's' : ''} — continue the conversation in Messages.` });
      setSelected(new Set());
      navigate(`/messages?partner=${userId}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({ title: 'Could not send request', description: typeof detail === 'string' ? detail : 'Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

  const goMonth = (delta) => {
    setCursor((c) => {
      let m = c.month + delta;
      let y = c.year;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { year: y, month: m };
    });
  };

  if (loading) {
    return (
      <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] animate-pulse h-64" data-testid="public-availability-loading" />
    );
  }

  return (
    <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]" data-testid="public-availability-calendar">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
          <CalendarBlank size={24} weight="bold" className="text-[#2B4C6F]" />
          Availability
        </h2>
        {status && (
          status.vacation_mode ? (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-xs font-bold uppercase">
              <Airplane size={14} weight="fill" /> On vacation
            </span>
          ) : status.available_today ? (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs font-bold uppercase" data-testid="public-availability-status">
              <CheckCircle size={14} weight="fill" /> Available today
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-300 text-xs font-bold uppercase">
              Booked today
            </span>
          )
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">Tap open days to request a booking</p>

      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => goMonth(-1)}
          className="w-9 h-9 flex items-center justify-center border-2 border-black rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="public-cal-prev-month"
          aria-label="Previous month"
        >
          <CaretLeft size={18} weight="bold" />
        </button>
        <span className="font-black text-gray-900">{MONTH_NAMES[cursor.month]} {cursor.year}</span>
        <button
          onClick={() => goMonth(1)}
          className="w-9 h-9 flex items-center justify-center border-2 border-black rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="public-cal-next-month"
          aria-label="Next month"
        >
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEK_DAYS.map((d, i) => (
          <div key={i} className="text-center text-xs font-bold text-gray-400 py-1">{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const iso = isoDate(cursor.year, cursor.month, d);
          const past = iso < today;
          const isToday = iso === today;
          const busy = isBlocked(iso);
          const isSelected = selected.has(iso);
          const selectable = !past && !busy;
          let cls = 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 cursor-pointer';
          if (past) cls = 'bg-gray-50 text-gray-300 border-gray-100 cursor-default';
          else if (busy) cls = 'bg-neutral-200 text-neutral-500 border-neutral-300 line-through cursor-not-allowed';
          if (isSelected) cls = 'bg-[#2B4C6F] text-white border-black cursor-pointer shadow-[2px_2px_0px_#000]';
          return (
            <button
              key={i}
              onClick={() => toggleSelect(iso)}
              disabled={!selectable}
              className={`aspect-square flex items-center justify-center text-sm font-bold rounded-lg border transition-colors ${cls} ${isToday ? 'ring-2 ring-[#2B4C6F]' : ''}`}
              title={past ? '' : busy ? 'Booked' : isSelected ? 'Selected — tap to remove' : 'Available — tap to select'}
              data-testid={selectable ? `cal-day-${iso}` : undefined}
            >
              {d}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-xs text-gray-600">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-300 inline-block" /> Available
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-neutral-200 border border-neutral-300 inline-block" /> Booked
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-[#2B4C6F] border border-black inline-block" /> Selected
        </span>
      </div>

      {/* Booking request bar */}
      {selected.size > 0 && (
        <div className="mt-4 p-4 bg-[#2B4C6F]/10 border-2 border-[#2B4C6F] rounded-xl" data-testid="booking-request-bar">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {Array.from(selected).sort().map((iso) => (
              <span key={iso} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-[#2B4C6F] rounded-lg text-xs font-bold text-[#33506B]">
                {prettyDate(iso)}
                <button onClick={() => toggleSelect(iso)} className="hover:text-red-600" aria-label={`Remove ${iso}`}>
                  <X size={12} weight="bold" />
                </button>
              </span>
            ))}
          </div>
          <button
            onClick={sendRequest}
            disabled={sending}
            className="w-full h-12 flex items-center justify-center gap-2 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50"
            data-testid="request-dates-btn"
          >
            {sending ? (
              <><SpinnerGap size={20} weight="bold" className="animate-spin" /> Sending...</>
            ) : (
              <><PaperPlaneTilt size={20} weight="bold" /> Request {selected.size} date{selected.size > 1 ? 's' : ''}</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
